import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  FormControl,
  IconButton,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Box,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import { useDispatch } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { TransactionStatusChange } from "../redux/Actions/transactionsService";
import { TransactionTable } from "../redux/Actions/transactionsService";

const TransactionModal = ({
  openTransModal,
  settransactionopenTransModal,
  selectedTransaction,
}) => {
  const dispatch = useDispatch();
  const [selectedStatus, setSelectedStatus] = useState("");
  const [submitError, setSubmitError] = useState(false);
  const [allowedStatusOptions, setAllowedStatusOptions] = useState([]);
  const role = localStorage.getItem("type");

  useEffect(() => {
    setSelectedStatus("");
    setSubmitError(false);
    if (selectedTransaction?.status === "PENDING") {
      setAllowedStatusOptions(["COMPLETED", "FAILED"]);
    } else {
      setAllowedStatusOptions([]);
    }
  }, [selectedTransaction]);

  const handleClose = () => {
    settransactionopenTransModal(false);
    setSelectedStatus("");
    setSubmitError(false);
  };

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
  };

  const handleSubmit = () => {
    if (!selectedStatus) {
      setSubmitError(true);
      return;
    }
    dispatch(
      TransactionStatusChange({
        id: selectedTransaction?.id,
        status: selectedStatus,
        role: role
      })
    ).then((response) => {
      if (response?.payload?.statusCode === 200) {
        dispatch(TransactionTable());
      }
    });
    handleClose();
  };

  const getStatusChip = (status) => {
    const statusLower = status?.toLowerCase();
    let backgroundColor = "";
    let color = "";
    let icon = null;

    switch (statusLower) {
      case "pending":
        backgroundColor = "#fef3c7";
        color = "#92400e";
        break;
      case "completed":
        backgroundColor = "#f0fdf4";
        color = "#166534";
        icon = <CheckCircleIcon sx={{ fontSize: 16 }} />;
        break;
      case "failed":
        backgroundColor = "#fef2f2";
        color = "#dc2626";
        icon = <CancelIcon sx={{ fontSize: 16 }} />;
        break;
      default:
        backgroundColor = "#f3f4f6";
        color = "#6b7280";
        break;
    }

    return (
      <Chip
        icon={icon}
        label={status}
        sx={{
          backgroundColor: backgroundColor,
          color: color,
          fontWeight: 600,
          fontSize: "12px",
          height: "28px",
          "& .MuiChip-icon": {
            color: color
          }
        }}
      />
    );
  };

  return (
    <Modal 
      open={openTransModal} 
      onClose={handleClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(4px)',
      }}
    >
      <Paper
        sx={{
          position: "relative",
          width: { xs: "90%", sm: 480 },
          maxWidth: 480,
          backgroundColor: "white",
          borderRadius: 3,
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            pb: 2,
              background: "#667eea",
            color: "white",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: "20px",
                color: "white",
              }}
            >
              Update Transaction Status
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{
                color: "white",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {/* Current Status */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: "#374151",
                mb: 1,
                fontSize: "14px",
              }}
            >
              Current Status
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {getStatusChip(selectedTransaction?.status)}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Status Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: "#374151",
                mb: 2,
                fontSize: "14px",
              }}
            >
              Select New Status
            </Typography>
            <FormControl fullWidth>
              {allowedStatusOptions.map((option) => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={selectedStatus === option}
                      onChange={handleStatusChange}
                      value={option}
                      sx={{
                        color: "#3b82f6",
                        "&.Mui-checked": {
                          color: "#3b82f6",
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {getStatusChip(option)}
                    </Box>
                  }
                  sx={{
                    mb: 1,
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: selectedStatus === option ? "#eff6ff" : "transparent",
                    border: selectedStatus === option ? "2px solid #3b82f6" : "2px solid transparent",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#f8fafc",
                    },
                  }}
                />
              ))}
              {submitError && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#dc2626",
                    mt: 1,
                    display: "block",
                    fontSize: "12px",
                  }}
                >
                  Please select a status before submitting.
                </Typography>
              )}
            </FormControl>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
              pt: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "14px",
                borderColor: "#d1d5db",
                color: "#6b7280",
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "#f9fafb",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!selectedStatus}
              sx={{
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "14px",
                backgroundColor: "#10b981",
                boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
                "&:hover": {
                  backgroundColor: "#059669",
                  boxShadow: "0 4px 12px rgba(16, 185, 129, 0.4)",
                },
                "&:disabled": {
                  backgroundColor: "#d1d5db",
                  color: "#9ca3af",
                },
              }}
            >
              Update Status
            </Button>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default TransactionModal;
